import React, { ReactElement, useEffect } from 'react';
import { gql } from '@apollo/client';
import { GetServerSidePropsContext } from 'next';
import {
  GetCommissionTierBySlugQuery,
  GetCommissionTierBySlugQueryVariables,
} from 'generated/graphql';
import { ContentPadding } from '@components/ContentPadding';
import { PageHeader } from '@components/Text';
import Layout from '@components/Layout';
import { getClient } from 'db/contentAPI';
import { Media } from '@components/Media';
import { COMMISSIONS } from 'logic/commissions';
import Link from 'next/link';
import { routes } from '@utils/routes';
import { CommissionTierBuyButton } from '@components/BuyButtons';
import { Button } from '@components/Button';
import { useRouter } from 'next/router';
import { MarkdownText } from '@components/MarkdownText';

export default function Projects({
  data,
}: {
  data: GetCommissionTierBySlugQuery;
  preview: boolean;
}): ReactElement {
  const commission = data?.commisionCollection?.items?.[0] ?? undefined;
  const list = data?.collectionCollection?.items;
  const featured = list?.slice(0, 1)?.[0];
  const router = useRouter();

  useEffect(() => {
    if (commission.slug === COMMISSIONS.community) {
      router.push('/community-commission/1');
    }
  }, [commission.slug, router]);

  return (
    <Layout
      head={{
        title: commission.name,
        description: commission.description,
        image: featured?.image.url,
      }}
    >
      <ContentPadding>
        <PageHeader className="mb-2">
          {commission?.name} Commission Tier
        </PageHeader>
        <div className="sm:grid sm:grid-cols-2 gap-y-28 text-black mb-8">
          <div className="text-left w-full">
            <div className="text-base w-8/12 whitespace-pre-line">
              <MarkdownText text={commission?.description} />
            </div>
          </div>
          <div className="container h-full mx-0 sm:relative">
            <div className="rounded-2xl sm:text-left sm:bottom-0">
              <CommissionTierBuyButton
                backgroundColor="bg-black"
                textColor="text-white"
                commissionSlug={commission.slug}
              />
              <div className="mt-4">
                <ViewPhotosButton commissionSlug={commission.slug} />
              </div>
            </div>
          </div>
          {/* {featured && (
            <div className="container h-full mx-0 sm:relative w-8/12">
              <div className=" rounded-2xl sm:text-lef sm:bottom-0">
                <p>Being claimed</p>
                <HeaderText>{featured?.name}</HeaderText>
                <p>
                  <SeasonNumber
                    commissionSlug={commission.slug}
                    seasonNumber={featured.season?.seasonNumber}
                  />
                </p>
                <p className="mt-4 mb-6">{featured.projectDescription}</p>
                <Button>
                  <NextLink
                    href={
                      featured.__typename === 'CommissionSeason'
                        ? `/community-commission/${featured.season.seasonNumber}`
                        : `/collections/${featured.slug}`
                    }
                  >
                    View Collection
                  </NextLink>
                </Button>
              </div>
            </div>
          )} */}
        </div>
      </ContentPadding>
      <ContentPadding className="mt-6">
        <div className="text-3xl font-semibold mb-2">
          Commission Photographers
        </div>
        <div className="sm:py-15 pt-4 pb-20">
          <div className="flex justify-around flex-col">
            {list.map((item) => (
              <div className="w-full flex mb-16" key={item.projectName}>
                <div className="flex-1 relative mr-6 sm:mr-12">
                  <Media
                    className="w-6/12 object-contain object-left"
                    height="100%"
                    width="100%"
                    layout="fill"
                    src={item.image.url}
                    alt=""
                  />
                </div>
                <div className="flex flex-col justify-end w-6/12">
                  <h2 className="text-left">{item.name}</h2>
                  <p className="no-i18n text-lg font-semibold">
                    {item.projectName}
                  </p>
                  <p>
                    <MarkdownText text={item.projectDescription} />
                  </p>
                  <Link href={routes.collectionDetails(item.slug)}>
                    <a className="hover:text-gray-600 underline mt-6">
                      View Collection
                    </a>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ContentPadding>
    </Layout>
  );
}

export function ViewPhotosButton({
  commissionSlug,
  backgroundColor,
  textColor,
}: {
  commissionSlug: string;
  backgroundColor?: string;
  textColor?: string;
}) {
  const buttonProps = {
    backgroundColor,
    color: textColor,
  };
  switch (commissionSlug) {
    case COMMISSIONS.curated:
      return (
        <Button {...buttonProps}>
          <a
            href="https://opensea.io/collection/obscura-curated"
            className="self-center"
            target="_blank"
            rel="noreferrer"
          >
            Obscura Curated Photos on Secondary
          </a>
        </Button>
      );
    case COMMISSIONS.magnum:
      return (
        <Button {...buttonProps}>
          <a
            href="https://opensea.io/collection/obscura-magnum"
            className="self-center"
            target="_blank"
            rel="noreferrer"
          >
            Obscura Magnum Photos on Secondary
          </a>
        </Button>
      );
    default:
      return null;
  }
}

const queryCommissionTierBySlug = gql`
  query GetCommissionTierBySlug($slug: String!, $preview: Boolean) {
    commisionCollection(where: { slug_contains: $slug }, preview: $preview) {
      items {
        slug
        name
        description
      }
    }
    commissionSeasonCollection(
      where: { commission: { slug_contains: $slug } }
      order: seasonNumber_DESC
      preview: $preview
    ) {
      items {
        slug
        name
        description
        name
        seasonNumber
      }
    }
    collectionCollection(
      where: { commission: { slug_contains: $slug }, image_exists: true }
      order: id_DESC
      preview: $preview
    ) {
      items {
        slug
        projectDescription
        projectName
        name
        type
        image {
          url
        }
        season {
          seasonNumber
        }
      }
    }
  }
`;

export async function getServerSideProps({
  res,
  preview,
  previewData,
  query: { slug },
}: GetServerSidePropsContext) {
  const { data, error } = await getClient(
    (previewData as { api: string })?.api
  ).query<GetCommissionTierBySlugQuery, GetCommissionTierBySlugQueryVariables>({
    query: queryCommissionTierBySlug,
    variables: {
      preview,
      slug: slug as string,
    },
  });

  if (!preview && !error) {
    res.setHeader(
      'Cache-Control',
      'public, s-maxage=60, stale-while-revalidate=59'
    );
  }

  return {
    props: {
      data,
      preview: preview ?? false,
    },
  };
}
